import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import styled from 'styled-components'
import { rem } from 'polished'
import type { RouterLocation } from 'connected-react-router'
import { replace } from 'connected-react-router'
import OfferPageGallery from '../OfferPageGallery'
import Pane from 'components/Common/Pane'
import Backdrop from 'components/utils/Backdrop'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import OfferIntro from './BundleAndSaveIntro'
import Breakpoint from 'components/Common/Breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import {
  fetchAvailableRatesForOffer,
} from 'actions/OfferActions'
import BundleAndSaveSidebarFilters from './BundleAndSaveSidebarFilters'
import OfferPageStateProvider from 'contexts/OfferPage/offerPageStateProvider'
import type { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { INITIAL_OFFER_PAGE_STATE, OfferPageActions, generateOfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import useMountedEffect from 'hooks/useMountedEffect'
import { clearBookingCalendar, fetchCalendars } from 'actions/CalendarActions'
import usePrevious from 'hooks/usePrevious'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import { urlHashDatePicker } from 'constants/url'
import { getInitialBundleAndSaveOfferPageState } from 'selectors/offerPage/initialOfferPageStateSelectors'
import Heading from 'LuxKit/Typography/Heading'
import { isValidOccupancy } from 'lib/offer/occupancyUtils'
import formatBundleLocationCaption from 'lib/offer/formatBundleLocationCaption'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import useIdleTimeReporter, { IdleTimeSource } from 'components/Recommendations/hooks/useIdleTimeReporter'
import useOfferPageReducer from 'hooks/OfferPage/useOfferPageReducer'

import { getGroupedBundledPackages } from 'lib/bundleAndSave/getGroupedBundledPackages'
import BundleAndSaveOptionsSection from './BundleAndSaveOptionsSection'
import Group from 'components/utils/Group'
import { sortBy, max, nonNullable } from 'lib/array/arrayUtils'
import BodyText from 'LuxKit/Typography/BodyText'
import OfferCapacityWithAgesSelect from '../Filters/CapacityWithAges/OfferCapacityWithAgesSelect'
import BundleAndSaveDateSelection from './BundleAndSaveDateSelection/BundleAndSaveDateSelection'
import BundleAndSaveSwapModal from 'components/OfferPage/BundleAndSave/BundleAndSaveSwapModal'
import { useScreenSizeOnly, useScreenSizeUp } from 'hooks/useScreenSize'
import BundleAndSaveHeader from './BundleAndSaveHeader'
import { isBundleAndSaveV2Enabled } from 'lib/offer/offerUtils'
import HotelOfferPageStickyFooter from '../HotelOfferPageStickyFooter'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { encodeSearchParams } from 'lib/search/searchUtils'
import useShareModalConfig from 'hooks/useShareModalConfig'
import moment from 'moment'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import OfferPageSignUpWall from 'components/Common/OfferPageSignUpWall'
import { getCheapestSelectedPackageOptionWithDefault } from 'selectors/offerPage/offerPageSelectors'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import useEuropeOfferPageWalledGarden from 'hooks/OfferPage/useEuropeOfferPagWalledGarden'
import { hasSelectedRooms } from 'lib/offer/offerPageUtil'

const RelativeBlock = styled.form`
  position: relative;
`

const BundleOptionSelectionSection = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(32)};
  width: 100%;
  background-color: ${(props) => props.theme.palette.neutral.surface.lightBackground};
  padding: ${rem(40)} 0;
`

type CartDefaults = Partial<OfferPageState>

interface Props {
  defaults: CartDefaults
  offer: App.BundleOffer
  routerLocation: RouterLocation<unknown>
  currency: string
}

// const lereTimingAnalyticsCategory = 'BundleAndSaveContent - LERE'
// const lereTimingType = 'lereBundleAndSaveRenderReady'

function BundleAndSaveContent(props: Props) {
  const {
    offer,
    defaults,
    routerLocation,
  } = props

  const {
    id,
    packages,
  } = offer

  // calendars only start fetching once the user has tried to look at dates
  // after that, try keep the calendars up to date
  const [viewedDates, setViewedDates] = useState(Object.values(defaults?.bundledOffers ?? {}).some(p => !!p.checkInDate))
  const [state, stateDispatch] = useOfferPageReducer(defaults)

  const availableForPurchase = useIsAvailableForPurchase(state)

  const bestPackage = useAppSelector(appState => getCheapestSelectedPackageOptionWithDefault(appState, state))
  const { hotelDiscountPercent, bundledDiscountPercent } = bestPackage
  const shouldShowDiscountPercent = hotelDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const shouldShowBundledDiscountPercent = bundledDiscountPercent >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE
  const showDiscountPill = availableForPurchase && !offer.isDiscountPillHidden && (shouldShowDiscountPercent && !offer.bundledWithFlightsOnly) || (offer.bundledWithFlightsOnly && shouldShowBundledDiscountPercent)
  const loggedOutSavedPercent = showDiscountPill ? offer.bundledWithFlightsOnly ? bundledDiscountPercent : hotelDiscountPercent : undefined

  const {
    airport,
    activeFilter,
    rooms,
    bundledOffers,
    bundleOffer,
    checkInDate,
    duration,
  } = state

  const [firstHotelOffer, secondHotelOffer] = useMemo(
    () => Object.values(bundledOffers ?? {}),
    [bundledOffers])

  const intl = useIntl()

  const selectYourDatesLabel = intl.formatMessage({
    id: 'vwwNiviBMXNOR6p2kotq',
    defaultMessage: 'Select your dates',
    description: 'Prompt text for date selection in bundle and save V2',
  })
  const whenDoYouWantToGoLabel = intl.formatMessage({
    id: 'Ni3cfySO6zLn9H2-o9Bm',
    defaultMessage: 'When do you want to go?',
    description: 'Prompt text for date selection in bundle and save on the offer detail page',
  })

  const isMobileScreen = useScreenSizeOnly('mobile')
  const dispatch = useAppDispatch()
  const packageOptionsAnchor = useRef<HTMLDivElement>(null)
  const bundledOffersAnchor = useRef<HTMLDivElement>(null)
  const scrollAfterFilterClose = useRef<boolean>(false)
  const previousState = usePrevious<OfferPageState>(state, INITIAL_OFFER_PAGE_STATE)
  const previousFilter = usePrevious(activeFilter)
  const lastFetchedRooms = useRef<Array<App.RoomOccupants> | null>(null)
  const lastFetchedAirport = useRef<App.Airport | null>(null)

  const isCalendarAlreadyOpen = Object.values(previousState.bundledOffers ?? {}).some((o) => o.checkInDate) || false

  const queryString = useMemo(() => {
    const isoCheckOut = checkInDate ? moment(checkInDate).add(duration, 'days').format(ISO_DATE_FORMAT) : undefined
    return encodeSearchParams({
      urlSearch: routerLocation.search,
      dates: {
        checkIn: checkInDate,
        checkOut: isoCheckOut,
      },
      rooms,
    }).toString()
  }, [checkInDate, duration, rooms, routerLocation.search])

  useIdleTimeReporter(IdleTimeSource.BundleAndSave)
  const isWalledGardenEuropeOffersEnabled = useEuropeOfferPageWalledGarden()

  const [stickyFooterVisible, setIsStickyFooterVisible] = useState<boolean>(true)
  const onStickyFooterToggle = useCallback((visible: boolean) => {
    setIsStickyFooterVisible(visible)
  }, [])

  useMountedEffect(() => {
    const previousCheckInDates = previousState?.bundledOffers ?? {}
    const datesChanges = !Object.values(state.bundledOffers ?? {}).every((o) => o.offer && previousCheckInDates[o.offer.id].checkInDate === o.checkInDate)

    scrollAfterFilterClose.current = scrollAfterFilterClose.current ||
      previousState.rooms !== rooms ||
      datesChanges
  }, [state.rooms, state.bundledOffers])

  const isTabletScreenSizeAndUp = useScreenSizeUp('tablet')

  const scrollToPackageOptions = useCallback(() => packageOptionsAnchor.current?.scrollIntoView(), [])

  // 'auto' scroll (not from a user action) only occurs on tablet and up sizes
  // as we don't want the users scroll to lose their position on mobile
  const autoScrollToOptions = useCallback(() => {
    if (isTabletScreenSizeAndUp) {
      scrollToPackageOptions()
    }
  }, [isTabletScreenSizeAndUp, scrollToPackageOptions])

  const scrollToBundledOffers = useCallback(() => {
    bundledOffersAnchor.current?.scrollIntoView()
  }, [])

  const fetchCalendarData = useCallback(() => {
    // if we have any invalid ages, don't try fetch - it'll fail anyway
    if (isValidOccupancy(...rooms)) {
      const packagesData = Object.values(getGroupedBundledPackages(offer, Object.values(bundledOffers ?? {}).map((bOffer) => ({
        offerId: bOffer.offer?.id ?? '',
        duration: bOffer.duration ?? 1,
      })))).flat()

      if (packagesData.length) {
        dispatch(fetchCalendars(id, {
          occupants: rooms,
          packages: packagesData,
        }))
      }
    }

    lastFetchedRooms.current = rooms
    lastFetchedAirport.current = airport ?? null
  }, [airport, offer, id, bundledOffers, dispatch, rooms])

  useEffect(() => {
    for (const bundledOffer of Object.values(bundledOffers ?? {})) {
      const useDynamicCancellationPolicies = bundledOffer.offer?.property?.useDynamicCancellationPolicies ?? false
      const occupanciesInvalid = rooms.some(item => item.childrenAge?.some(age => age < 0))
      if (bundleOffer && !!bundledOffer.checkInDate && !occupanciesInvalid && !activeFilter && useDynamicCancellationPolicies && bundledOffer.offer) {
        dispatch(fetchAvailableRatesForOffer(
          bundledOffer.offer,
          {
            occupants: rooms,
            checkIn: bundledOffer.checkInDate,
            checkOut: moment(bundledOffer.checkInDate).add(bundledOffer.duration, 'days').format(ISO_DATE_FORMAT),
            bundleOfferId: bundleOffer.id,
          },
        ))
      }
    }
  }, [dispatch, bundledOffers, bundleOffer, activeFilter, rooms])

  useEffect(() => {
    const hasRooms = hasSelectedRooms(rooms)
    const hasDates = Object.values(state?.bundledOffers ?? {}).every((o) => o.checkInDate)
    const hasInvalidChildren = !isValidOccupancy(...rooms)

    if (previousFilter === 'capacity') {
      if (!hasRooms) {
        // user looked at capacity, didn't change anything and closed it
        // we consider this them applying the default rooms, so dispatch a proper update
        stateDispatch({
          type: OfferPageActions.setRooms,
          rooms: [generateOfferPageRoom()],
        })

        if (!activeFilter) {
          autoScrollToOptions()
          scrollAfterFilterClose.current = false
        }
      }
      if (!activeFilter && !hasDates && !hasInvalidChildren) {
        stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
      }
    }

    if (activeFilter === 'checkin' && hasInvalidChildren) {
      // trying to open the calendar with invalid children ages, means we can't fetch calendar
      // direct them to the capacity instead to fill it in
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
    }

    if (previousFilter === 'checkin' && !activeFilter && hasDates && !hasRooms) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
    }

    if (activeFilter) {
      if (activeFilter === 'checkin') {
        scrollToBundledOffers()
      } else {
        autoScrollToOptions()
      }
    }

    if (previousFilter && !activeFilter && hasRooms && hasDates) {
      if (scrollAfterFilterClose.current) {
        if (isMobileScreen && firstHotelOffer?.checkInDate && secondHotelOffer?.checkInDate) {
          const firstPricingBlock = document.getElementsByClassName('pricing')[0]?.getBoundingClientRect()
          const winScroll =
            document.body.scrollTop || document.documentElement.scrollTop
          const winHeight = window.innerHeight
          if (winScroll && firstPricingBlock) {
            const adjust = winHeight ? winHeight - firstPricingBlock.height - 40 : 40
            window.scrollTo({
              behavior: 'auto',
              top: winScroll + firstPricingBlock.top - adjust,
            })
          }
        } else {
          autoScrollToOptions()
        }
      }
      scrollAfterFilterClose.current = false
    }

    if (previousFilter && !activeFilter && hasDates && state?.bundledOffers) {
      const firstBundledOfferId = Object.keys(state.bundledOffers)[0]
      const sortedBundledOffers = sortBy(Object.values(state.bundledOffers), (p) => {
        return p.checkInDate ? moment(p.checkInDate).toDate().getTime() : 0
      }, 'asc')

      if (firstBundledOfferId !== sortedBundledOffers[0].offer?.id) {
        stateDispatch({ type: OfferPageActions.reverseBundles })
      }
    }

    if (activeFilter === 'checkin' && !viewedDates) {
      setViewedDates(true)
      fetchCalendarData()
    } else {
      if (viewedDates && lastFetchedRooms.current !== rooms) {
        dispatch(clearBookingCalendar())
        fetchCalendarData()
      }
    }
  }, [activeFilter, firstHotelOffer, secondHotelOffer]) // oxlint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // once we load packages and have dates, get our calendar data
    if (viewedDates && offer.packages) {
      fetchCalendarData()
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [offer.packages])

  useEffect(() => {
    if (defaults.offer && defaults.bundledOffers && previousState?.bundledOffers) {
      if (previousState?.offer?.packages?.length !== defaults.offer.packages.length) {
        const bundledOffersUpdates = Object.keys(defaults.bundledOffers).reduce((acc, itemKey) => {
          const { checkInDate, checkOutDate, duration } = previousState?.bundledOffers?.[itemKey] ?? {}

          if (checkInDate && checkOutDate) {
            acc[itemKey].checkOutDate = checkOutDate
            acc[itemKey].checkInDate = checkInDate
            acc[itemKey].duration = duration
          }

          return acc
        }, defaults.bundledOffers)

        stateDispatch({ type: OfferPageActions.setOffer, offer: defaults.offer, bundledOffers: bundledOffersUpdates })
      }
    }
  }, [stateDispatch, defaults, previousState])

  useMountedEffect(() => {
    // effectively listening on 'on duration change' event
    if (viewedDates) {
      fetchCalendarData()
    }

    if (!activeFilter) {
      autoScrollToOptions()
    }
  }, [bundledOffers])

  useEffect(() => {
    if (routerLocation.hash === urlHashDatePicker) {
      stateDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    }
  }, []) // oxlint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    dispatch(replace({ ...routerLocation, search: queryString }))
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [queryString])

  const occupancyLimits = useMemo(() => {
    const groupedByOffers = Object.values(bundledOffers ?? {}).map((o) => packages.filter(p => p.offerId === o.offer?.id).flatMap((p) => p.roomRate.capacities))

    const maxAdults = nonNullable(groupedByOffers.map(x => max(x, o => o.adults)?.adults))
    const maxChildren = Math.min(...groupedByOffers.map(x => max(x, o => o.children)?.children ?? 0))
    const maxInfants = Math.min(...groupedByOffers.map(x => max(x, o => o.infants)?.infants ?? 0))

    const [maxAdultsLimit, maxAdultsIndex] = maxAdults.reduce<[number, number]>((acc, item, index) => {
      const [val] = acc

      if ((val ?? 0) > (item ?? 0) || !val) {
        return [item, index]
      }

      return acc
    }, [0, 0])

    const capacities = groupedByOffers[maxAdultsIndex] ?? []

    return capacities.filter(o => o.adults <= maxAdultsLimit && o.children <= maxChildren && o.infants <= maxInfants)
  }, [packages, bundledOffers])

  const filtersWrapperRef = useRef<HTMLDivElement>(null)

  // Keep bundled destinations compact while still using a locale-aware conjunction.
  const locationCaption = useMemo(
    () => formatBundleLocationCaption(
      intl,
      Object.values(offer.bundledOffers).map((bundledOffer) => ({
        locationHeading: bundledOffer.locationHeading,
        locationSubheading: bundledOffer.locationSubheading,
      })),
    ),
    [intl, offer],
  )

  const shareConfig = useShareModalConfig({
    image: offer.images[0],
    offerId: offer.id,
    location: locationCaption,
    type: 'offer',
    offerType: 'hotel',
    utmCampaign: 'webshare',
    queryString,
  })

  return (
    <OfferPageStateProvider state={state} dispatch={stateDispatch}>
      <RelativeBlock onSubmit={(e) => e.preventDefault()}>
        {availableForPurchase &&
          <Breakpoint max="mobile">
            <BundleAndSaveSidebarFilters
              onViewPackageOptions={autoScrollToOptions}
              capacities={occupancyLimits}
            />
          </Breakpoint>}
        <CSSBreakpoint min="tablet">
          <BundleAndSaveHeader onViewPackageOptions={scrollToPackageOptions} />
        </CSSBreakpoint>
        <OfferPageGallery offer={offer} shareModalConfig={shareConfig} autoPlayVideo={!!offer.isVideoVimeoHero} />
        <Pane>
          <OfferIntro
            onViewPackageOptions={scrollToPackageOptions}
            shareConfig={shareConfig}
          />
        </Pane>
        <ScrollAnchor ref={packageOptionsAnchor} />
        <SoftOfferPageWalledGarden>
          <BundleOptionSelectionSection id="#Package-Options">
            <LayoutContainer>
              <Group direction="vertical" horizontalAlign="center" gap={24}>
                <Heading variant="heading3" format="titlecase" as="h2" align="center">
                  {intl.formatMessage({
                    id: 'j4-lI9-ZeFUvHTdwGN6u',
                    defaultMessage: 'Bundle Options',
                    description: 'Heading for the bundle and save package selection section',
                  })}
                </Heading>
                {availableForPurchase && (<>
                  <Group ref={filtersWrapperRef} fullWidth direction="horizontal" horizontalAlign="center">
                    <OfferCapacityWithAgesSelect capacities={occupancyLimits} disableMultiRoom boundaryRef={filtersWrapperRef} />
                  </Group>
                  <Group fullWidth direction="vertical" horizontalAlign="center" gap={12}>
                    <BodyText variant="large">
                      {isBundleAndSaveV2Enabled() ? selectYourDatesLabel : whenDoYouWantToGoLabel}
                    </BodyText>
                    <ScrollAnchor ref={bundledOffersAnchor} />
                    <Group fullWidth direction="horizontal" horizontalAlign="center">
                      <BundleAndSaveDateSelection
                        onClickLabel={intl.formatMessage({
                          id: 'pVOBt5rPu2awislUIRM3',
                          defaultMessage: 'Select dates',
                          description: 'Button label for date selection in bundle and save',
                        })}
                        isCalendarAlreadyOpen={isCalendarAlreadyOpen}
                      />
                    </Group>
                    <Backdrop visible={activeFilter === 'capacity' || activeFilter === 'checkin'} />
                  </Group>
                </>)}
              </Group>
            </LayoutContainer>
            <BundleAndSaveOptionsSection
              availableForPurchase={availableForPurchase}
              onStickyFooterToggle={onStickyFooterToggle}
            />
          </BundleOptionSelectionSection>
          {availableForPurchase &&
            <HotelOfferPageStickyFooter
              visible={stickyFooterVisible}
              lowestPricePackage={offer.lowestPriceBundlePackage ?? offer.packages[0]}
              onScrollTo={scrollToPackageOptions}
              offer={offer}
            />}
        </SoftOfferPageWalledGarden>
        <BundleAndSaveSwapModal />
      </RelativeBlock>
      <SoftOfferPageWalledGarden bypassWall={isWalledGardenEuropeOffersEnabled} wall={<OfferPageSignUpWall offerType="hotel" saveUpToPercent={loggedOutSavedPercent} />} />
    </OfferPageStateProvider>
  )
}

function mapStateToProps(state: App.State, ownProps: Partial<Props>) {
  return {
    defaults: getInitialBundleAndSaveOfferPageState(state, ownProps.offer!),
    routerLocation: state.router.location,
    currency: state.geo.currentCurrency,
  }
}

export default connect(mapStateToProps)(BundleAndSaveContent)
